import React, { forwardRef } from 'react';
import GeneralDialog from '../../internal-components/GeneralDialog';
import styled from 'styled-components/macro';
import InputBase from '../form/InputBase';
import useTranslations from '../../hooks/useTranslations';
import Select from '../engine/SelectEngine';
import Colors from '../../internal-components/Colors';
import CharacterCounter from '../../internal-components/CharacterCounter';

const H2 = styled.h2`
  margin: 2rem 0 0.8rem;
`;

const Red = styled.sup`
  color: ${Colors.OT_RED};
`;

const ValidationDiv = styled.div`
  font-size: small;
  height: 1rem;
  position: absolute;
`;

const ErrorMsg = styled.p`
  padding: 0.8em 0;
  font-size: 14px;
`;

const FormKeyDescription = styled.div`
  font-size: 14px;
  padding: 0 0 0.8em;
`;

const FormKeyValidationIndicatorView = props => {
  const t = useTranslations();
  const { formKeyInvalid, formKeyValidated, formKeyValidating, isNew } = props;
  if (!isNew) {
    return (
      <ErrorMsg data-error="foo">
        {t('form-builder.form_overview.form-key-invariable')}
      </ErrorMsg>
    );
  }
  return (
    <ErrorMsg data-error="foo">
      {!formKeyValidating && formKeyValidated && formKeyInvalid ? (
        formKeyInvalid === 'invalid' ? (
          <>{t('form-builder.form_overview.form-key-invalid')}</>
        ) : formKeyInvalid === 'unique' ? (
          <>{t('form-builder.form_overview.form-key-not-unique')}</>
        ) : (
          ''
        )
      ) : (
        ''
      )}
    </ErrorMsg>
  );
};

const FormBuilderOverviewDialogView = forwardRef((props, dialogRef) => {
  const t = useTranslations();
  return (
    <GeneralDialog
      open={props.isOpen}
      handleClose={props.onDialogCancel}
      handleConfirm={props.onDialogConfirm}
      buttons={props.buttons}
    >
      <H2>
        {t('form-builder.form_overview.form-title')}
        <Red>*</Red>
      </H2>
      <InputBase
        value={props.title}
        onChange={e => props.setTitle(e.target.value)}
        placeholder={t('form-builder.form_overview.form-title.placeholder')}
        inputProps={{ maxLength: 100, 'data-testid': 'form-title-input' }}
      />
      <CharacterCounter string={props.title} maxLength={100} />
      <div ref={dialogRef}>
        <H2>
          {t('form-builder.form_overview.form-key')}
          <Red>*</Red>
        </H2>
        <FormKeyDescription>
          {t('form-builder.form_overview.form-key-subtext')}
        </FormKeyDescription>
        <InputBase
          disabled={!props.isNew}
          value={props.formKey}
          onBlur={props.onFormKeyBlur}
          onChange={e => props.setFormKey(e.target.value)}
          inputProps={{ 'data-testid': 'form-title-form-key' }}
        />
        <FormKeyValidationIndicatorView
          formKeyInvalid={props.formKeyInvalid}
          formKeyValidated={props.formKeyValidated}
          formKeyValidating={props.formKeyValidating}
          isNew={props.isNew}
        />
      </div>
      <H2>{t('form-builder.form_overview.form-description')}</H2>
      <InputBase
        value={props.description}
        onChange={e => props.setDescription(e.target.value)}
        multiline
        rows={6}
        inputProps={{
          maxLength: 5000,
          'data-testid': 'form-description-input'
        }}
      />
      <CharacterCounter string={props.description} maxLength={5000} />
      {!!props.folderOptions.length && (
        <>
          <H2>{t('form-builder.form_folder')}</H2>
          <Select
            fullWidth
            data-testid="folder-selector"
            placeholder={'form-builder.cancel_folder_selection'}
            value={props.folder}
            onChange={props.setFolder}
            options={[
              {
                value: null,
                text: 'form-builder.cancel_folder_selection',
                'data-testid': `folder-selector-none`
              },
              ...props.folderOptions.map(f => ({
                value: f.id,
                text: f.name,
                'data-testid': `folder-selector-${f.id}`
              }))
            ]}
          />
        </>
      )}
    </GeneralDialog>
  );
});

export default FormBuilderOverviewDialogView;
